import React, { useState, useEffect, useMemo } from "react";
import MainModal from "components/ModalMain";
import Input from "components/Input";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import DatePicker from "components/DatePicker";
import TimePicker from "components/TimePicker";
import Checkbox from "components/Checkbox";
import { useMutation, useLazyQuery, useQuery } from "@apollo/client";
import {
  ADD_BOOKING_TO_GROUP,
  GET_AVAILABLE_ROOM_TYPES_BY_DATE_RANGE,
  GET_ALL_BOOKING_ADD_ONS,
} from "graphql/service";
import {
  Head,
  Container,
  Row,
  TravelInputRow,
  SummaryContainer,
  ItemContainer,
  Footer,
} from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";
import { dateDiff } from "utils/util";
import moment from 'moment-timezone';
import { HST_RATE, MAT_RATE, TIME_ZONE } from 'constants/constant';
import colors from "constants/colors";
import {
  DEFAULT_CHECK_IN_HOUR,
  DEFAULT_CHECK_IN_MINUTE,
  DEFAULT_CHECK_OUT_HOUR,
  DEFAULT_CHECK_OUT_MINUTE
} from 'constants/constant';

const ADDON_IDS = {
  BREAKFAST: "63f6e8f8-8095-4c79-8d92-ce5812db4f15",
  PARKING: "fd67a4cb-667a-4ba9-bec4-379cb430682a",
  EARLY_CHECKIN: "4c773a2c-0d0a-44ba-8680-d34244fea4d5",
  LATE_CHECKOUT: "c5032166-bd7f-4a8a-bc4b-81d5c7fc5ff5",
};

const ModalAddRoom = ({ hidden, buttonStyle, groupId, refetch }) => {
  
  const [isOpen, setIsOpen] = useState(false);
  const [barrierFree, setBarrierFree] = useState(false);
  const [roomTypeId, setRoomTypeId] = useState("");
  const [breakfastQuantity, setBreakfastQuantity] = useState(0);
  const [parkingQuantity, setParkingQuantity] = useState(0);
  const [earlyCheckIn, setEarlyCheckIn] = useState(false);
  const [lateCheckOut, setLateCheckOut] = useState(false);
  
  const [checkInDate, setCheckInDate] = useState(
    moment().tz(TIME_ZONE).set({ hour: DEFAULT_CHECK_IN_HOUR, minute: DEFAULT_CHECK_IN_MINUTE, second: 0 })
  );
  const [checkOutDate, setCheckOutDate] = useState(
    moment().tz(TIME_ZONE).add(1, "days").set({ hour: DEFAULT_CHECK_OUT_HOUR, minute: DEFAULT_CHECK_OUT_MINUTE, second: 0 })
  );

  const { data: addonData, error: addonError } = useQuery(GET_ALL_BOOKING_ADD_ONS);

  const addonPrices = useMemo(() => {
    if (!addonData?.validBookingAddons?.length) return {};

    return addonData.validBookingAddons.reduce((acc, item) => {
      acc[item.title] = item.pricePerNight;
      return acc;
    }, {});
  }, [addonData]);

  const addOns = useMemo(
    () => [
      { id: ADDON_IDS.BREAKFAST, quantity: Number(breakfastQuantity), title: "Breakfast" },
      { id: ADDON_IDS.PARKING, quantity: Number(parkingQuantity), title: "Parking" },
      { id: ADDON_IDS.EARLY_CHECKIN, quantity: earlyCheckIn ? 1 : 0, title: "Early Checkin" },
      { id: ADDON_IDS.LATE_CHECKOUT, quantity: lateCheckOut ? 1 : 0, title: "Late Checkout" },
    ],
    [breakfastQuantity, parkingQuantity, earlyCheckIn, lateCheckOut]
  );

  const [getAvailableRoomTypesByDateRange, { data: availableRoomTypesData, error: availableRoomTypesError }] = useLazyQuery(
    GET_AVAILABLE_ROOM_TYPES_BY_DATE_RANGE
  );

  useEffect(() => {

    if (!checkInDate || !checkOutDate) return;
    getAvailableRoomTypesByDateRange({
      variables: {
        input: {
          checkInDate: checkInDate.toDate(),
          checkOutDate: checkOutDate.toDate(),
          isWheelchairAccessible: barrierFree,
          roomQuantity: 1,
          guestQuantity: 1,
        },
      },
    });
  }, [
    barrierFree,
    checkInDate,
    checkOutDate,
    getAvailableRoomTypesByDateRange,
  ]);

  const roomTypeOptions = useMemo(
    () =>
      availableRoomTypesData?.availableRoomTypesByDateRange?.map((rt) => ({
        value: rt.roomTypeId,
        label: rt.name,
      })) ?? [],
    [availableRoomTypesData]
  );

  useEffect(() => {
    if (!addonError || !availableRoomTypesError) return;

    if (!addonError.networkError && !availableRoomTypesError.networkError) {
      let content = addonError.message || availableRoomTypesError.networkError.message;

      if (addonError.message.includes('Validation') || availableRoomTypesError.message.includes('Validation')) {
        content = JSON.stringify(
          addonError.graphQLErrors[0]?.extensions?.exception || availableRoomTypesError.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [addonError, availableRoomTypesError]);

  const billArray = useMemo(() => {

    if (!roomTypeId || !availableRoomTypesData) return [];
    const availableRoomTypes = availableRoomTypesData.availableRoomTypesByDateRange
    const nights = dateDiff(checkInDate, checkOutDate);
    const selectedRoomType = availableRoomTypes.find(
      (roomType) => roomType.roomTypeId === roomTypeId
    );
    if (!selectedRoomType) return [];

    let subTotal = 0;
    const MAT = (selectedRoomType.pricePerNight * nights) * MAT_RATE
    const billArray = [];
    billArray.push([
      <ItemContainer key={roomTypeId}>
        <span className="description">{`${selectedRoomType?.name} - ${nights + (nights>1? ' nights,': ' night,')} ${
          selectedRoomType?.bed
        } ${selectedRoomType.hasWindows ? "w/ window": selectedRoomType.hasSkylight? "w/ skylight": ""}`}</span>
        <span className="value">
          {"$" + (selectedRoomType.pricePerNight * nights).toFixed(2)}
        </span>
      </ItemContainer>,
      <ItemContainer key="MAT">
        <span className="description">MAT</span>
        <span className="value">
          {"$" + MAT.toFixed(2)}
        </span>
      </ItemContainer>,
    ]);
    subTotal = subTotal + selectedRoomType.pricePerNight * nights + MAT;

    addOns.forEach((item) => {
      if (item.quantity > 0) {
        switch (item.id) {
          case "63f6e8f8-8095-4c79-8d92-ce5812db4f15": // Breakfast
            billArray.push(
              <ItemContainer key={item.id}>
                <span className="description">{`Breakfast (${
                  item.quantity > 1
                    ? item.quantity + " vouchers"
                    : item.quantity + " voucher"
                } @ $${addonPrices["Breakfast"]}/meal)`}</span>
                <span className="value">{"$" + item.quantity * addonPrices["Breakfast"]}</span>
              </ItemContainer>
            );
            subTotal = subTotal + item.quantity * addonPrices["Breakfast"];
            break;
          case "fd67a4cb-667a-4ba9-bec4-379cb430682a": // Parking
            billArray.push(
              <ItemContainer key={item.id}>
                <span className="description">{`Parking (${
                  item.quantity + (item.quantity > 1 ? " cars, " : " car, ")
                } ${nights + (nights > 1 ? "nights" : "night")} @ $${
                  addonPrices["Parking"]
                }/car/night)`}</span>
                <span className="value">
                  {"$" + item.quantity * nights * addonPrices["Parking"]}
                </span>
              </ItemContainer>
            );
            subTotal = subTotal + item.quantity * nights * addonPrices["Parking"];
            break;
          case "4c773a2c-0d0a-44ba-8680-d34244fea4d5": // Early Check-in
            billArray.push(
              <ItemContainer key={item.id}>
                <span className="description">{`Early Checkin (${item.quantity} room @ $${addonPrices["Early Checkin"]}/room)`}</span>
                <span className="value">
                  {"$" + item.quantity * addonPrices["Early Checkin"]}
                </span>
              </ItemContainer>
            );
            subTotal = subTotal + item.quantity * addonPrices["Early Checkin"];
            break;
          case "c5032166-bd7f-4a8a-bc4b-81d5c7fc5ff5": // Late Check-out
            billArray.push(
              <ItemContainer key={item.id}>
                <span className="description">{`Late Checkout (${item.quantity} room @ $${addonPrices["Late Checkout"]}/room)`}</span>
                <span className="value">
                  {"$" + item.quantity * addonPrices["Late Checkout"]}
                </span>
              </ItemContainer>
            );
            subTotal = subTotal + item.quantity * addonPrices["Late Checkout"];
            break;
          default:
            return;
        }
      } else {
        return;
      }
    });
    const HST = subTotal * HST_RATE;
    const total = subTotal + HST;
    billArray.push(
      <ItemContainer key="HST">
        <span className="description">HST</span>
        <span className="value">
          {"$" + HST.toFixed(2)}
        </span>
      </ItemContainer>,
      <ItemContainer key="total">
        <span className="description">Total Change</span>
        <span className="value">
          {"$" + total.toFixed(2)}
        </span>
      </ItemContainer>
    )

    return billArray;
  }, [
    roomTypeId,
    availableRoomTypesData,
    addonPrices,
    checkInDate,
    checkOutDate,
    addOns
  ]);

  const [addBookingToGroup, { loading }] = useMutation(ADD_BOOKING_TO_GROUP, {
    onCompleted: (data) => {
      refetch();
      setIsOpen(false);
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message
        if(error.message.includes('Validation')) {
            content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
        }
        debouncedErrorMessage(content)
      }
    },
  });

  const handleAddRoom = () => {
    if(loading) return
    addBookingToGroup({
      variables: {
        groupId: groupId,
        input: {
          roomTypeId: roomTypeId,
          checkInDate: checkInDate.toDate(),
          checkOutDate: checkOutDate.toDate(),
          isWheelchairAccessible: barrierFree,
          addons: addOns,
        },
      },
    });
  };

  return (
    <MainModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="modal-success"
      buttonLabel="Add Room"
      buttonStyle={buttonStyle}
      hidden={hidden}
      additionalstyle={`
        max-width: 640px;
        .modal-content{        
          .close {
            z-index: 200;
          }
          .modal-header {
            position: relative;
            top: 10px;
          }              
          .modal-body {
            height: 100%;
            padding: 24px;
          }
        }
        top: 69px;
      `}
    >
      <Head>Add another room to this booking?</Head>
      <Container>
        <Checkbox
          checked={barrierFree}
          onChange={() => setBarrierFree(!barrierFree)}
          title="Barrier Free"
          additionalstyle={`
            margin: 24px 0 0 2px;
          `}
        />
        <TravelInputRow>
          <div className="checkIn">
            <div className="datePickerContainer">
              <span>ARRIVAL</span>
              <DatePicker
                error={moment(checkInDate).tz(TIME_ZONE) >= moment(checkOutDate).tz(TIME_ZONE)}
                minDate={moment(new Date()).tz(TIME_ZONE)}
                dateValue={moment(checkInDate).tz(TIME_ZONE)}
                dateOnChange={(e) => {
                  setCheckInDate(e);
                  if (moment(e).tz(TIME_ZONE) >= moment(checkOutDate).tz(TIME_ZONE)) {
                    setCheckOutDate(
                      moment(e)
                        .tz(TIME_ZONE)
                        .add(1, "days")
                        .set({ hour: DEFAULT_CHECK_OUT_HOUR, minute: DEFAULT_CHECK_OUT_MINUTE, second: 0 })
                    );
                  }
                }}
                additionalstyle={`
                  width: 100%;
                  min-width: 130px;
                  border: 1px solid ${colors.darkGrey};

                  input{
                    padding-right: 26px;
                  }
                `}
              />
            </div>
            <div className="timePickerContainer">
              <span>ARRIVAL TIME</span>
              <TimePicker
                TimeValue={moment(checkInDate).tz(TIME_ZONE)}
                setTime={setCheckInDate}
                additionalstyle={`
                  width: 100%;
                  min-width: 110px;
                  border: 1px solid ${colors.darkGrey};

                  input{
                    padding-right: 26px;
                  }
                `}
              />
            </div>
          </div>
          <div className="checkOut">
            <div className="datePickerContainer">
              <span>DEPARTURE</span>
              <DatePicker
                error={moment(checkInDate).tz(TIME_ZONE) >= moment(checkOutDate).tz(TIME_ZONE)}
                minDate={moment(new Date()).tz(TIME_ZONE)}
                dateValue={moment(checkOutDate).tz(TIME_ZONE)}
                dateOnChange={(e) => {
                  setCheckOutDate(e);
                  if (moment(e).tz(TIME_ZONE) <= moment(checkInDate).tz(TIME_ZONE)) {
                    if (
                      moment(e)
                        .tz(TIME_ZONE)
                        .subtract(1, "days")
                        .set({ hour: DEFAULT_CHECK_IN_HOUR, minute: DEFAULT_CHECK_IN_MINUTE, second: 0 }) >=
                      moment(new Date()).tz(TIME_ZONE)
                    ) {
                      setCheckInDate(
                        moment(e)
                          .tz(TIME_ZONE)
                          .subtract(1, "days")
                          .set({ hour: DEFAULT_CHECK_IN_HOUR, minute: DEFAULT_CHECK_IN_MINUTE, second: 0 })
                      );
                    } else {
                      setCheckInDate(moment(new Date()).tz(TIME_ZONE));
                    }
                  }
                }}
                additionalstyle={`
                  width: 100%;
                  min-width: 130px;
                  border: 1px solid ${colors.darkGrey};

                  input{
                    padding-right: 26px;
                  }
                `}
              />
            </div>
            <div className="timePickerContainer">
              <span>DEPARTURE TIME</span>
              <TimePicker
                TimeValue={moment(checkOutDate).tz(TIME_ZONE)}
                setTime={setCheckOutDate}
                additionalstyle={`
                  width: 100%;
                  min-width: 110px;
                  border: 1px solid ${colors.darkGrey};

                  input{
                    padding-right: 26px;
                  }
                `}
              />
            </div>
          </div>
        </TravelInputRow>
        <Row>
          <Dropdown
            mainPage
            options={roomTypeOptions}
            value={
              roomTypeOptions.find((option) => option.value === roomTypeId)
                ?.label || ""
            }
            placeholder="-Select-"
            title="ROOM TYPE"
            onChange={(e) => {
              setRoomTypeId(e);
            }}
            additionalstyle={`
              flex-basis: 25%;
              margin-right: 16px;
            `}
          />
          <Input
            mainPage
            title="BREAKFAST"
            value={breakfastQuantity}
            onChange={(e) => setBreakfastQuantity(e.target.value)}
            additionalstyle={`
              flex-basis: 15%;
              margin-right: 16px;
            `}
          />
          <Input
            mainPage
            title="PARKING"
            value={parkingQuantity}
            onChange={(e) => setParkingQuantity(e.target.value)}
            additionalstyle={`
              flex-basis: 15%;
            `}
          />
        </Row>
        <Checkbox
          checked={earlyCheckIn}
          onChange={() => setEarlyCheckIn(!earlyCheckIn)}
          title="Early Check-in"
          additionalstyle={`
            margin: 24px 0 0 2px;
          `}
        />
        <Checkbox
          checked={lateCheckOut}
          onChange={() => setLateCheckOut(!lateCheckOut)}
          title="Late Check-out"
          additionalstyle={`
            margin: 16px 0 0 2px;
          `}
        />
        {billArray.length ? (
          <SummaryContainer>{billArray}</SummaryContainer>
        ) : null}
        <Footer>
          <Button
            danger
            additionalstyle={`
              width: 79px;
            `}
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            success
            additionalstyle={`
              width: 106px;
              margin-left: 16px;
            `}
            onClick={handleAddRoom}
            data-loading={loading}
          >
            {loading? null: 'Add Room'}
          </Button>
        </Footer>
      </Container>
    </MainModal>
  );
};

export default ModalAddRoom;
